import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

export interface ProgramListItem {
  id: number;
  name: string | null;
  webFormDisplayName: string | null;
  programShortCode: string | null;
  certificateCode: string | null;
  noOfAcademicYears: number | null;
  openForRegistration: number | null;
  enableStatus: number | null;
  sortOrder: number | null;
  intakeCount: number;
  termCount: number;
}

export interface ProgramIntake {
  mappingId: number;
  yearId: number;
  yearName: string | null;
  yearCode: string | null;
  intakeDisplayName: string | null;
  openForRegistration: number | null;
}

export interface ProgramTerm {
  id: number;
  name: string | null;
  displayName: string | null;
  startDate: string | null;
  endDate: string | null;
}

export interface ProgramModule {
  mappingId: number;
  courseId: number | null;
  courseName: string | null;
  courseShortCode: string | null;
  yearId: number | null;
  yearName: string | null;
  termId: number | null;
  termName: string | null;
  credits: number | null;
  startDate: string | null;
  endDate: string | null;
  disableStatus: number | null;
}

export interface ProgramDetail {
  id: number;
  name: string | null;
  webFormDisplayName: string | null;
  transcriptDisplayName: string | null;
  programShortCode: string | null;
  programShortCode2: string | null;
  certificateCode: string | null;
  versionId: number | null;
  minDob: string | null;
  maxDob: string | null;
  noOfAcademicYears: number | null;
  noOfBatches: number | null;
  openForRegistration: number | null;
  enableStatus: number | null;
  sortOrder: number | null;
  enquiryType2Restriction: string | null;
  censusDuration: number | null;
  contractDuration: number | null;
  schoolIds: number[];
  streamIds: number[];
  testCategoryIds: number[];
  universityIds: number[];
  learningOptionIds: number[];
  disciplineIds: number[];
  intakes: ProgramIntake[];
  terms: ProgramTerm[];
  modules: ProgramModule[];
}

export interface ProgramFormData {
  name: string;
  webFormDisplayName?: string | null;
  transcriptDisplayName?: string | null;
  programShortCode?: string | null;
  programShortCode2?: string | null;
  certificateCode?: string | null;
  versionId?: number | null;
  minDob?: string | null;
  maxDob?: string | null;
  noOfAcademicYears?: number | null;
  noOfBatches?: number | null;
  openForRegistration?: number | null;
  enableStatus?: number;
  sortOrder?: number | null;
  enquiryType2Restriction?: string | null;
  censusDuration?: number | null;
  contractDuration?: number | null;
  schoolIds?: number[];
  streamIds?: number[];
  testCategoryIds?: number[];
  universityIds?: number[];
  learningOptionIds?: number[];
  disciplineIds?: number[];
}

export interface ProgramLookups {
  years: { id: number; name: string; yearCode: string | null }[];
  terms: { id: number; name: string; programId: number | null }[];
  courses: { id: number; name: string; shortCode: string | null }[];
  schools: { id: number; name: string }[];
  programTypes: { id: number; name: string }[];
  streams: { id: number; name: string }[];
  testCategories: { id: number; name: string }[];
  universities: { id: number; name: string }[];
  learningOptions: { id: number; name: string }[];
  disciplines: { id: number; name: string }[];
}

class ProgramManagementService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/programs/manage`;
  }

  async getLookups(): Promise<{ success: boolean; data: ProgramLookups }> {
    try {
      const r = await myAxios.get(`${this.baseUrl}/lookups`);
      return r.data;
    } catch (err: any) {
      console.error("[ProgramManagementService] Error fetching lookups:", err);
      return { success: false, data: { years: [], terms: [], courses: [], schools: [], programTypes: [], streams: [], testCategories: [], universities: [], learningOptions: [], disciplines: [] } };
    }
  }

  async searchPrograms(params: {
    page?: number;
    limit?: number;
    search?: string;
    name?: string;
    code?: string;
    schoolId?: number;
    programTypeId?: number;
    yearId?: number;
    includeInactive?: boolean;
    sortBy?: string;
    sortDir?: "asc" | "desc";
  }): Promise<{ success: boolean; data: { programs: ProgramListItem[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> {
    try {
      const r = await myAxios.get(`${this.baseUrl}/search`, { params });
      return r.data;
    } catch (err: any) {
      console.error("[ProgramManagementService] Error searching:", err);
      return { success: false, data: { programs: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 } } };
    }
  }

  async getProgramById(id: number): Promise<{ success: boolean; data?: ProgramDetail; message?: string }> {
    try {
      const r = await myAxios.get(`${this.baseUrl}/${id}`);
      return r.data;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to fetch program" };
    }
  }

  async createProgram(data: ProgramFormData): Promise<{ success: boolean; data?: { id: number }; message?: string }> {
    try {
      const r = await myAxios.post(this.baseUrl, data);
      return r.data as any;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to create program" };
    }
  }

  async updateProgram(id: number, data: ProgramFormData): Promise<{ success: boolean; message?: string }> {
    try {
      const r = await myAxios.put(`${this.baseUrl}/${id}`, data);
      return r.data as any;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to update program" };
    }
  }

  async deleteProgram(id: number): Promise<{ success: boolean; message?: string }> {
    try {
      const r = await myAxios.delete(`${this.baseUrl}/${id}`);
      return r.data;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to delete program" };
    }
  }

  async mapIntake(programId: number, body: { yearId: number; intakeDisplayName?: string | null; openForRegistration?: number }): Promise<{ success: boolean; data?: { mappingId: number }; message?: string }> {
    try {
      const r = await myAxios.post(`${this.baseUrl}/${programId}/intakes`, body);
      return r.data as any;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to map intake" };
    }
  }

  async unmapIntake(programId: number, mappingId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const r = await myAxios.delete(`${this.baseUrl}/${programId}/intakes/${mappingId}`);
      return r.data;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to unmap intake" };
    }
  }

  async addTerm(programId: number, body: { name: string; displayName?: string | null; startDate?: string | null; endDate?: string | null; yearId?: number | null }): Promise<{ success: boolean; data?: { termId: number }; message?: string }> {
    try {
      const r = await myAxios.post(`${this.baseUrl}/${programId}/terms`, body);
      return r.data as any;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to add term" };
    }
  }

  async addModule(programId: number, body: { courseId: number; yearId: number; termId?: number | null; credits?: number | null; startDate?: string | null; endDate?: string | null }): Promise<{ success: boolean; data?: { mappingId: number }; message?: string }> {
    try {
      const r = await myAxios.post(`${this.baseUrl}/${programId}/modules`, body);
      return r.data as any;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to add module" };
    }
  }

  async getModulesByIntake(programId: number, yearId: number): Promise<{ success: boolean; data?: { modules: any[]; termDates?: Array<{ termId: number; startDate: string | null; endDate: string | null }> }; message?: string }> {
    try {
      const r = await myAxios.get(`${this.baseUrl}/${programId}/intakes/${yearId}/modules`);
      return r.data as any;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to fetch modules" };
    }
  }

  async removeModule(programId: number, mappingId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const r = await myAxios.delete(`${this.baseUrl}/${programId}/modules/${mappingId}`);
      return r.data;
    } catch (err: any) {
      return { success: false, message: (err.response?.data?.error?.message || err.response?.data?.message) || "Failed to remove module" };
    }
  }
}

export default new ProgramManagementService();
